import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBackNavigate } from '@/shared/hooks/use-back-navigate';
import {
  Button,
  Dropdown,
  PageContainer,
  SurfaceCard,
  type DropdownMenuEntry,
} from '@react-apps/design-system';
import { useUser } from '../hooks/use-user';
import { useProfileActions } from '../hooks/use-profile-actions';
import { useAccountActions } from '../hooks/use-account-actions';
import { extractApiErrorCode, SCOPE_ID } from '@/lib/api-client';
import { useIdentityActions } from '../hooks/use-identity-actions';
import {
  useCanImpersonate,
  useCanImpersonateOnMedq,
} from '@/shared/impersonation/use-can-impersonate';
import { ActAsOnMedqButton } from '@/shared/impersonation/ActAsOnMedqButton';
import {
  useCanAdministerUser,
  useIsScopedProvisioner,
} from '../hooks/use-user-permissions';
import { ScopedAccessButton } from '../components/ScopeSelect';
import { useProvisionableScopes } from '@/shared/scopes/use-scopes';
import {
  useImpersonationStore,
  type ImpersonationTarget,
} from '@/shared/impersonation/impersonation.store';
import { ImpersonateModal } from '@/shared/impersonation/ImpersonateModal';
import {
  endAllImpersonations,
  type StartedImpersonation,
} from '@/shared/impersonation/impersonation-api';
import { useAuthStore } from '@/modules/auth/presentation/store/auth.store';
import { EditUserModal } from '../components/EditUserModal';
import { CreatePlatformProfileModal } from '../components/CreatePlatformProfileModal';
import { UserEntityEventsModal } from '../components/UserEntityEventsModal';
import type {
  UserPlatformDetail,
  AuthMethodKind,
} from '../../domain/entities/user.entity';
import { SetTemporaryPasswordModal } from '../components/SetTemporaryPasswordModal';
import { ProfilePerksEditorModal } from '../components/ProfilePerksEditorModal';
import {
  detectDocumentType,
  formatDocumentForDisplay,
  formatPhoneForDisplay,
  formatZipCodeForDisplay,
} from '@/shared/utils/br-fields';
import { DocumentValidityBadge } from '@/shared/components/document-validity-badge';
import { DuplicateDocumentBadge } from '../components/DuplicateDocumentBadge';
import { ScopeBadge } from '@/shared/scopes/ScopeBadge';
import { platformBadge } from '@/shared/scopes/platform-colors';

const GENDER_LABEL: Record<string, string> = {
  MALE: 'Masculino',
  FEMALE: 'Feminino',
  OTHER: 'Outro',
};

const DOCUMENT_TYPE_LABEL: Record<'cpf' | 'cnpj', string> = {
  cpf: 'CPF',
  cnpj: 'CNPJ',
};

const AUTH_METHOD_LABEL: Partial<Record<AuthMethodKind, string>> = {
  password: 'Senha',
  'google.com': 'Google',
  'microsoft.com': 'Microsoft',
};

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-(--color-text-secondary) text-xs font-medium uppercase tracking-wide">
        {label}
      </p>
      <p className="text-(--color-text-primary) text-sm">
        {value ?? (
          <span className="text-(--color-text-secondary) italic">
            Não informado
          </span>
        )}
      </p>
    </div>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <SurfaceCard className="space-y-5">
      <h2 className="text-(--color-text-primary) text-sm font-semibold">
        {title}
      </h2>
      {children}
    </SurfaceCard>
  );
}

function PlatformCard({
  platform,
  onActivate,
  onDeactivate,
  onManagePerks,
  busy,
  canAdminister,
  actAsTarget,
}: {
  platform: UserPlatformDetail;
  onActivate: () => void;
  onDeactivate: () => void;
  onManagePerks: () => void;
  busy: boolean;
  /** Sem isso, sobra só "Gerenciar vantagens" no menu de ações. */
  canAdminister: boolean;
  /** Quem a máscara veste, quando o operador pode abri-la no MedQ. */
  actAsTarget: ImpersonationTarget | null;
}) {
  const { color: bgClass, initial } = platformBadge(platform.platform);

  return (
    <div className="border-(--color-border) bg-(--color-bg-secondary) flex items-start gap-4 rounded-lg border p-4">
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${bgClass} ${platform.isActive ? 'opacity-100' : 'opacity-40'}`}
      >
        {initial}
      </div>
      <div className="min-w-0 flex-1 space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-(--color-text-primary) text-sm font-medium">
            {platform.platformName}
          </span>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
              platform.isActive
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400'
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${platform.isActive ? 'bg-green-500' : 'bg-neutral-400'}`}
            />
            {platform.isActive ? 'Ativa' : 'Inativa'}
          </span>
          <div className="ml-auto">
            <Dropdown
              align="right"
              trigger={
                <button
                  type="button"
                  disabled={busy}
                  className="text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-(--color-bg-primary) rounded-md px-2 py-1 text-sm disabled:opacity-40"
                >
                  Ações ▾
                </button>
              }
              items={[
                ...(canAdminister
                  ? [
                      platform.isActive
                        ? {
                            id: 'deactivate',
                            label: 'Desativar',
                            onClick: onDeactivate,
                          }
                        : {
                            id: 'activate',
                            label: 'Ativar',
                            onClick: onActivate,
                          },
                    ]
                  : []),
                {
                  id: 'perks',
                  label: 'Gerenciar vantagens',
                  onClick: onManagePerks,
                },
              ]}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
          <Field label="Perfil" value={platform.profileName} />
          <Field label="Platform" value={platform.platform} />
          <Field label="Scope" value={<ScopeBadge id={platform.scopeId} />} />
          <Field label="Criado em" value={formatDate(platform.createdAt)} />
          <Field label="Atualizado em" value={formatDate(platform.updatedAt)} />
          <Field
            label="Último acesso"
            value={
              platform.lastAccessAt
                ? formatDate(platform.lastAccessAt)
                : 'Nunca'
            }
          />
        </div>
        {actAsTarget &&
          platform.platform === 'aristo-medq' &&
          platform.isActive && (
            <div className="flex items-center gap-3">
              <ActAsOnMedqButton
                target={actAsTarget}
                scopeId={platform.scopeId}
                url={platform.url ?? null}
              />
            </div>
          )}
      </div>
    </div>
  );
}

function SkeletonCard({ rows = 3 }: { rows?: number }) {
  return (
    <SurfaceCard className="space-y-4">
      <div className="h-4 w-32 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700" />
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="h-4 animate-pulse rounded bg-neutral-100 dark:bg-neutral-800"
            style={{ width: `${60 + (i % 3) * 15}%` }}
          />
        ))}
      </div>
    </SurfaceCard>
  );
}

/** Feedback exibido na barra de ações da conta (sucesso/erro), num único lugar. */
type AccountActionFeedback = { tone: 'success' | 'error'; text: string };

/**
 * Traduz o erro de uma ação de conta em mensagem pro operador. O caso que mais
 * confunde é o 422 do "reset" numa conta não-verificada: a saída certa não é
 * verificar-e-depois-resetar, é o **convite de primeiro acesso** (que já
 * verifica a conta quando a pessoa define a senha).
 */
function describeAccountActionError(err: unknown): string {
  switch (extractApiErrorCode(err)) {
    case 'ACCOUNT_EMAIL_NOT_VERIFIED':
      return 'Conta ainda não verificada. Use "Reenviar convite" — ao definir a senha pelo link, a conta é verificada automaticamente (não precisa verificar antes).';
    case 'INSUFFICIENT_ACCOUNT_AUTHORITY':
      return 'Você não tem autoridade para operar a credencial desta conta.';
    default:
      return 'Não foi possível concluir a ação. Tente novamente.';
  }
}

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const goBack = useBackNavigate('/management/users');
  const { data: user, isLoading, isError } = useUser(userId ?? '');
  const docType = detectDocumentType(user?.doc);
  const { activate, deactivate } = useProfileActions(userId ?? '');
  const profileActionBusy = activate.isPending || deactivate.isPending;
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCreateProfileOpen, setIsCreateProfileOpen] = useState(false);
  const [isTempPwOpen, setIsTempPwOpen] = useState(false);
  const [actionFeedback, setActionFeedback] =
    useState<AccountActionFeedback | null>(null);
  const [isEventsOpen, setIsEventsOpen] = useState(false);
  /** Perfil cujas vantagens estão sendo editadas — o editor abre sobre a página. */
  const [perksTarget, setPerksTarget] = useState<UserPlatformDetail | null>(
    null,
  );
  const accountActions = useAccountActions(userId ?? '');
  const { unlinkIdentity } = useIdentityActions(userId ?? '');
  const queryClient = useQueryClient();
  const canImpersonate = useCanImpersonate();
  const canImpersonateOnMedq = useCanImpersonateOnMedq();
  /**
   * Separa "abre a página" de "age sobre a pessoa": o `iamEditor` entra aqui só
   * para gerenciar acessos. Ver `use-user-permissions.ts`.
   */
  const canAdminister = useCanAdministerUser();
  /** O iamEditor dá acesso só às plataformas que administra. */
  const scopedProvisioner = useIsScopedProvisioner();
  const provisionableScopes = useProvisionableScopes(scopedProvisioner);
  // Sob máscara não se abre outra: o backend recusa começar sessão mascarado.
  const isImpersonating = useImpersonationStore((s) => s.target !== null);
  const myEmail = useAuthStore((s) => s.auth?.email);
  const [impersonateTarget, setImpersonateTarget] =
    useState<ImpersonationTarget | null>(null);
  const endAllSupportSessions = useMutation({
    mutationFn: endAllImpersonations,
  });
  /**
   * Operador é quem tem perfil neste painel, ativo ou não: desativar o perfil é
   * justamente quando se quer derrubar as sessões que ele deixou abertas.
   */
  const isOperator = (user?.platforms ?? []).some(
    (p) => p.scopeId === SCOPE_ID,
  );

  const startImpersonation = useImpersonationStore((s) => s.startImpersonation);
  const handleImpersonationStarted = (session: StartedImpersonation) => {
    startImpersonation({ ...session, target: impersonateTarget! });
    setImpersonateTarget(null);
    void queryClient.clear();
    void navigate('/');
  };

  /**
   * Chamado no clique: apaga o feedback da ação anterior — no cabeçalho, longe
   * do item que o gerou, ele pareceria resultado da ação nova.
   */
  const feedbackHandlers = <T,>(successText: (r: T) => string) => {
    setActionFeedback(null);
    return {
      onSuccess: (r: T) =>
        setActionFeedback({ tone: 'success', text: successText(r) }),
      onError: (err: unknown) =>
        setActionFeedback({
          tone: 'error',
          text: describeAccountActionError(err),
        }),
    };
  };

  /**
   * Todas as ações sobre a pessoa num menu só, filtradas pelo que quem vê a
   * página pode fazer — item sem permissão não aparece, em vez de aparecer
   * desabilitado.
   */
  const actionItems: DropdownMenuEntry[] = !user
    ? []
    : [
        ...(canImpersonate && !isImpersonating && user.email !== myEmail
          ? [
              {
                id: 'impersonate',
                label: 'Atuar como',
                onClick: () =>
                  setImpersonateTarget({
                    userId: user.userId,
                    name: user.name,
                  }),
              },
            ]
          : []),
        {
          id: 'events',
          label: 'Histórico',
          onClick: () => setIsEventsOpen(true),
        },
        ...(canAdminister
          ? [
              {
                id: 'edit',
                label: 'Editar',
                onClick: () => setIsEditOpen(true),
              },
              { id: 'account-divider', divider: true as const },
              // Reset só faz sentido com e-mail já verificado — o back recusa
              // (422) conta não-verificada. Para essas, o caminho é "Reenviar
              // convite", que verifica ao definir a senha.
              ...(user.isVerified
                ? [
                    {
                      id: 'reset-password',
                      label: 'Reenviar e-mail de reset de senha',
                      disabled: accountActions.resetPassword.isPending,
                      onClick: () =>
                        accountActions.resetPassword.mutate(
                          user.accountId,
                          feedbackHandlers(
                            () => 'E-mail de redefinição de senha enviado.',
                          ),
                        ),
                    },
                  ]
                : [
                    {
                      id: 'resend-verification',
                      label: 'Enviar e-mail de verificação',
                      disabled: accountActions.resendVerification.isPending,
                      onClick: () =>
                        accountActions.resendVerification.mutate(
                          user.accountId,
                          feedbackHandlers(
                            () => 'E-mail de verificação enviado.',
                          ),
                        ),
                    },
                    {
                      id: 'resend-invite',
                      label: 'Reenviar convite',
                      disabled: accountActions.resendInvite.isPending,
                      onClick: () =>
                        accountActions.resendInvite.mutate(
                          user.accountId,
                          feedbackHandlers((r: { sent: boolean }) =>
                            r.sent
                              ? 'Convite reenviado.'
                              : 'Esta pessoa já definiu a senha — nada foi enviado.',
                          ),
                        ),
                    },
                  ]),
              {
                id: 'temp-password',
                label: 'Forçar troca de senha',
                onClick: () => setIsTempPwOpen(true),
              },
              ...(isOperator
                ? [
                    {
                      id: 'end-support-sessions',
                      label: 'Encerrar sessões de suporte ativas',
                      disabled: endAllSupportSessions.isPending,
                      onClick: () =>
                        endAllSupportSessions.mutate(
                          user.userId,
                          feedbackHandlers((r: { ended: number }) =>
                            r.ended === 0
                              ? 'Esta pessoa não tinha sessão de suporte ativa.'
                              : `${r.ended} ${r.ended === 1 ? 'sessão de suporte encerrada' : 'sessões de suporte encerradas'}.`,
                          ),
                        ),
                    },
                  ]
                : []),
            ]
          : []),
      ];

  if (isError) {
    return (
      <PageContainer maxWidth="4xl" spacing="none">
        <SurfaceCard padding="xl" className="text-center">
          <p className="text-(--color-text-primary) font-medium">
            Usuário não encontrado
          </p>
          <p className="text-(--color-text-secondary) mt-1 text-sm">
            O usuário solicitado não existe ou você não tem permissão para
            visualizá-lo.
          </p>
          <button
            onClick={goBack}
            className="text-(--color-accent) mt-4 text-sm hover:underline"
          >
            Voltar para lista
          </button>
        </SurfaceCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={goBack}
          className="text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center gap-1.5 text-sm transition-colors"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5 8.25 12l7.5-7.5"
            />
          </svg>
          Usuários
        </button>
      </div>

      {isLoading ? (
        <>
          <SurfaceCard className="flex items-center gap-5">
            <div className="h-16 w-16 animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-700" />
            <div className="space-y-2">
              <div className="h-5 w-48 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700" />
              <div className="h-4 w-64 animate-pulse rounded bg-neutral-100 dark:bg-neutral-800" />
            </div>
          </SurfaceCard>
          <SkeletonCard rows={4} />
          <SkeletonCard rows={3} />
        </>
      ) : user ? (
        <>
          {/* Identity header */}
          <SurfaceCard className="flex flex-wrap items-center gap-5">
            <div className="bg-(--color-accent)/10 text-(--color-accent) flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-2xl font-bold">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-(--color-text-primary) text-xl font-semibold">
                  {user.name}
                </h1>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                    user.isVerified
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${user.isVerified ? 'bg-green-500' : 'bg-amber-500'}`}
                  />
                  {user.isVerified ? 'Verificado' : 'Não verificado'}
                </span>
              </div>
              <p className="text-(--color-text-secondary) text-sm">
                {user.email}
              </p>
              {/* Montado desde o início (o leitor de tela só anuncia mudança
                  numa região `aria-live` que já existia) e com altura
                  reservada, para a mensagem não empurrar a página. */}
              {canAdminister && (
                <p
                  role="status"
                  aria-live="polite"
                  className={`min-h-4 text-xs ${
                    actionFeedback?.tone === 'error'
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-green-600 dark:text-green-400'
                  }`}
                >
                  {actionFeedback?.text ?? ''}
                </p>
              )}
            </div>
            <Dropdown
              align="right"
              items={actionItems}
              trigger={
                // Mesmo desenho do botão "Filtros" das listagens.
                <span
                  title="Ações"
                  className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) inline-flex items-center rounded-lg border px-3 py-2.5 transition"
                >
                  <span className="sr-only">Ações</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <circle cx="5" cy="12" r="1.75" />
                    <circle cx="12" cy="12" r="1.75" />
                    <circle cx="19" cy="12" r="1.75" />
                  </svg>
                </span>
              }
            />
          </SurfaceCard>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Conta */}
            <Card title="Conta">
              <div className="grid grid-cols-1 gap-4">
                <Field
                  label="Account ID"
                  value={
                    <span className="font-mono text-xs">{user.accountId}</span>
                  }
                />
                <Field
                  label="User ID"
                  value={
                    <span className="font-mono text-xs">{user.userId}</span>
                  }
                />
                <Field label="E-mail" value={user.email} />
                <Field
                  label="Verificado em"
                  value={user.verifiedAt ? formatDate(user.verifiedAt) : null}
                />
                <Field
                  label="Conta criada em"
                  value={formatDate(user.accountCreatedAt)}
                />
                <Field
                  label="Perfil criado em"
                  value={formatDate(user.userCreatedAt)}
                />
                <Field
                  label="Última atualização"
                  value={formatDate(user.userUpdatedAt)}
                />
              </div>
            </Card>

            <div className="space-y-6">
              {/* Perfil */}
              <Card title="Perfil">
                <div className="grid grid-cols-1 gap-4">
                  <Field label="Nome" value={user.name} />
                  <div className="grid grid-cols-2 gap-4">
                    <Field
                      label={`Documento${docType ? ` (${DOCUMENT_TYPE_LABEL[docType]})` : ''}`}
                      value={
                        user.doc && (
                          <span className="flex items-center gap-2">
                            {formatDocumentForDisplay(user.doc)}
                            <DocumentValidityBadge value={user.doc} />
                            <DuplicateDocumentBadge userId={user.userId} />
                          </span>
                        )
                      }
                    />
                    <Field
                      label="Telefone"
                      value={formatPhoneForDisplay(user.phone)}
                    />
                  </div>
                  <Field
                    label="Gênero"
                    value={user.gender ? GENDER_LABEL[user.gender] : null}
                  />
                </div>
              </Card>

              {/* Endereço */}
              <Card title="Endereço">
                {!user.address ? (
                  <p className="text-(--color-text-secondary) text-sm italic">
                    Não informado
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Logradouro" value={user.address.street} />
                      <Field label="Número" value={user.address.number} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Bairro" value={user.address.neighborhood} />
                      <Field
                        label="Complemento"
                        value={user.address.complement}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Cidade" value={user.address.city} />
                      <Field label="Estado" value={user.address.state} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="País" value={user.address.country} />
                      <Field
                        label="CEP"
                        value={formatZipCodeForDisplay(user.address.zipCode)}
                      />
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </div>

          {/* Plataformas */}
          <Card title={`Plataformas (${(user.platforms ?? []).length})`}>
            {canAdminister && (
              <div className="flex justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsCreateProfileOpen(true)}
                >
                  Criar perfil de plataforma
                </Button>
              </div>
            )}
            {scopedProvisioner && (
              <div className="flex justify-end">
                <ScopedAccessButton
                  source={provisionableScopes}
                  onClick={() => setIsCreateProfileOpen(true)}
                  compact
                >
                  Dar acesso a uma plataforma
                </ScopedAccessButton>
              </div>
            )}
            {(user.platforms ?? []).length === 0 ? (
              <p className="text-(--color-text-secondary) text-sm italic">
                Nenhuma plataforma vinculada.
              </p>
            ) : (
              <div className="space-y-3">
                {(user.platforms ?? []).map((p) => (
                  <PlatformCard
                    key={p.id}
                    platform={p}
                    busy={profileActionBusy}
                    onActivate={() => activate.mutate(p.id)}
                    onDeactivate={() => deactivate.mutate(p.id)}
                    onManagePerks={() => setPerksTarget(p)}
                    canAdminister={canAdminister}
                    actAsTarget={
                      canImpersonateOnMedq &&
                      !isImpersonating &&
                      user.email !== myEmail
                        ? { userId: user.userId, name: user.name }
                        : null
                    }
                  />
                ))}
              </div>
            )}
          </Card>

          {/* Métodos de login */}
          <Card title={`Métodos de login (${(user.authMethods ?? []).length})`}>
            {(user.authMethods ?? []).length === 0 ? (
              <p className="text-(--color-text-secondary) text-sm italic">
                Nenhum método de login vinculado.
              </p>
            ) : (
              <>
                <ul className="space-y-2">
                  {(user.authMethods ?? []).map((m) => {
                    const isLast = (user.authMethods ?? []).length <= 1;
                    return (
                      <li
                        key={m.method}
                        className="border-(--color-border) flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2.5"
                      >
                        <div className="space-y-0.5">
                          <p className="text-(--color-text-primary) text-sm font-medium">
                            {AUTH_METHOD_LABEL[m.method] ?? m.method}
                          </p>
                          <p className="text-(--color-text-secondary) text-xs">
                            Vinculado em {formatDate(m.linkedAt)}
                          </p>
                        </div>
                        {canAdminister && (
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={isLast || unlinkIdentity.isPending}
                            onClick={() =>
                              unlinkIdentity.mutate({
                                accountId: user.accountId,
                                method: m.method,
                              })
                            }
                          >
                            Desvincular
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {canAdminister && (user.authMethods ?? []).length <= 1 && (
                  <p className="text-(--color-text-secondary) text-xs">
                    Não é possível desvincular o único método de login.
                  </p>
                )}
                {unlinkIdentity.error && (
                  <p className="text-sm text-red-600">
                    {unlinkIdentity.error instanceof Error
                      ? unlinkIdentity.error.message
                      : 'Falha ao desvincular método'}
                  </p>
                )}
              </>
            )}
          </Card>
        </>
      ) : null}

      {impersonateTarget && (
        <ImpersonateModal
          target={impersonateTarget}
          onClose={() => setImpersonateTarget(null)}
          onStarted={handleImpersonationStarted}
        />
      )}

      {isEditOpen && user && (
        <EditUserModal user={user} open onClose={() => setIsEditOpen(false)} />
      )}

      {isCreateProfileOpen && user && (
        <CreatePlatformProfileModal
          userId={user.userId}
          userName={user.name}
          userEmail={user.email}
          open
          onClose={() => setIsCreateProfileOpen(false)}
          provisionableScopes={
            scopedProvisioner ? provisionableScopes : undefined
          }
        />
      )}
      {user && (
        <SetTemporaryPasswordModal
          userId={userId ?? ''}
          accountId={user.accountId}
          userName={user.name}
          open={isTempPwOpen}
          onClose={() => setIsTempPwOpen(false)}
        />
      )}
      {perksTarget && (
        <ProfilePerksEditorModal
          profileId={perksTarget.id}
          profileLabel={perksTarget.profileName ?? perksTarget.platformName}
          scopeId={perksTarget.scopeId}
          open
          onClose={() => setPerksTarget(null)}
        />
      )}
      {user && (
        <UserEntityEventsModal
          accountId={user.accountId}
          userId={user.userId}
          platformProfileIds={(user.platforms ?? []).map((p) => p.id)}
          open={isEventsOpen}
          onClose={() => setIsEventsOpen(false)}
        />
      )}
    </PageContainer>
  );
}
